import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * AnimatedThemeToggler — Magic UI (ported to React JS)
 *
 * Usa a View Transitions API para criar uma transição circular "ripple"
 * a partir do ponto de clique ao alternar entre dark/light mode.
 *
 * Fallback automático para navegadores sem suporte à View Transitions API.
 */
export function AnimatedThemeToggler({ className = '', duration = 400 }) {
  const { isDarkMode, toggleTheme } = useTheme();

  const handleToggle = (e) => {
    const x = e.clientX;
    const y = e.clientY;

    // Fallback: sem View Transitions API (Firefox, navegadores antigos)
    if (!document.startViewTransition) {
      toggleTheme();
      return;
    }

    const transition = document.startViewTransition(() => {
      toggleTheme();
    });

    transition.ready.then(() => {
      const radius = Math.hypot(
        Math.max(x, window.innerWidth - x),
        Math.max(y, window.innerHeight - y)
      );

      const clipPath = [
        `circle(0px at ${x}px ${y}px)`,
        `circle(${radius}px at ${x}px ${y}px)`,
      ];

      // Dark → Light: círculo cresce na nova view (light)
      // Light → Dark: círculo cresce na nova view (dark)
      document.documentElement.animate(
        { clipPath },
        {
          duration,
          easing: 'ease-in-out',
          pseudoElement: '::view-transition-new(root)',
        }
      );
    });
  };

  return (
    <button
      onClick={handleToggle}
      className={`relative w-10 h-10 flex items-center justify-center rounded-lg text-zinc-400 hover:bg-surface-container-high transition-colors overflow-hidden group ${className}`}
      aria-label={isDarkMode ? 'Ativar modo claro' : 'Ativar modo escuro'}
      title={isDarkMode ? 'Modo Claro' : 'Modo Escuro'}
    >
      {/* Wrapper com flip animation */}
      <div
        className="relative w-5 h-5"
        style={{
          transition: `transform ${duration}ms cubic-bezier(0.4,0,0.2,1)`,
          transformStyle: 'preserve-3d',
        }}
      >
        {/* Sol — visível no dark mode (clicando vai para light) */}
        <span
          className="material-symbols-outlined absolute inset-0 flex items-center justify-center text-xl leading-none transition-all duration-300"
          style={{
            opacity: isDarkMode ? 1 : 0,
            transform: isDarkMode ? 'rotate(0deg) scale(1)' : 'rotate(-90deg) scale(0.5)',
            color: '#fbbf24',
          }}
        >
          light_mode
        </span>

        {/* Lua — visível no light mode (clicando vai para dark) */}
        <span
          className="material-symbols-outlined absolute inset-0 flex items-center justify-center text-xl leading-none transition-all duration-300"
          style={{
            opacity: isDarkMode ? 0 : 1,
            transform: isDarkMode ? 'rotate(90deg) scale(0.5)' : 'rotate(0deg) scale(1)',
            color: '#818cf8',
          }}
        >
          dark_mode
        </span>
      </div>
    </button>
  );
}
